// APIKeyModal.jsx
// Users enter their own Anthropic API key to enable the AI assistant.
// The key is stored in localStorage only — validated via the Firebase proxy,
// never sent directly to api.anthropic.com from the browser.

import React, { useState, useEffect } from 'react';
import { X, Key, ExternalLink, Eye, EyeOff, CheckCircle } from 'lucide-react';

const AI_PROXY_URL = 'https://us-central1-geneie513.cloudfunctions.net/anthropicProxy';

export const STORAGE_KEY = 'user_anthropic_api_key';

export const getStoredApiKey = () => {
  try { return localStorage.getItem(STORAGE_KEY) || ''; }
  catch { return ''; }
};

export const setStoredApiKey = (key) => {
  try {
    if (key) localStorage.setItem(STORAGE_KEY, key);
    else localStorage.removeItem(STORAGE_KEY);
  } catch {}
};

const APIKeyModal = ({ onClose, onKeySaved }) => {
  const [key, setKey] = useState('');
  const [show, setShow] = useState(false);
  const [error, setError] = useState('');
  const [testing, setTesting] = useState(false);

  useEffect(() => {
    const existing = getStoredApiKey();
    if (existing) setKey(existing);
  }, []);

  const validate = (k) => k.trim().startsWith('sk-ant-');

  const handleSave = async () => {
    const trimmed = key.trim();
    if (!trimmed) { setError('Please enter an API key.'); return; }
    if (!validate(trimmed)) {
      setError('Key should start with sk-ant- — check you copied the full key.');
      return;
    }
    setError('');
    setTesting(true);
    try {
      // Validate the key via the proxy (avoids CORS block on direct calls)
      const res = await fetch(AI_PROXY_URL, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'X-API-Key': trimmed },
        body: JSON.stringify({
          model: 'claude-sonnet-4-6',
          max_tokens: 5,
          messages: [{ role: 'user', content: 'hi' }],
        }),
      });
      const data = await res.json();
      if (res.status === 401 || data?.error?.type === 'authentication_error') {
        setError('Invalid key — Anthropic returned an authentication error.');
        setTesting(false);
        return;
      }
    } catch {
      // Network error — save anyway, will fail gracefully in the assistant
    }
    setStoredApiKey(trimmed);
    setTesting(false);
    onKeySaved && onKeySaved(trimmed);
    onClose();
  };

  const handleClear = () => {
    setStoredApiKey('');
    setKey('');
    onKeySaved && onKeySaved('');
    onClose();
  };

  const existing = getStoredApiKey();

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-2xl w-full max-w-md shadow-2xl">

        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-slate-200">
          <div className="flex items-center gap-3">
            <div className="w-9 h-9 bg-indigo-100 rounded-xl flex items-center justify-center">
              <Key className="w-5 h-5 text-indigo-600" />
            </div>
            <div>
              <h3 className="text-base font-bold text-slate-800">AI Assistant Setup</h3>
              <p className="text-xs text-slate-500">Enter your Anthropic API key</p>
            </div>
          </div>
          <button onClick={onClose} className="p-1.5 hover:bg-slate-100 rounded-lg transition-colors">
            <X className="w-4 h-4 text-slate-500" />
          </button>
        </div>

        <div className="p-6 space-y-4">
          {/* Explanation */}
          <div className="bg-indigo-50 border border-indigo-200 rounded-xl p-4">
            <p className="text-sm text-indigo-800 leading-relaxed">
              GENEie's AI assistant is powered by Anthropic Claude. To use it,
              enter your own API key below. Your key is stored only in your browser
              and routed through a secure proxy — it never touches GENEie's database.
            </p>
          </div>

          {/* Key input */}
          <div>
            <label className="block text-sm font-semibold text-slate-700 mb-1.5">
              Anthropic API Key
            </label>
            <div className="flex items-center gap-2 border border-slate-300 rounded-xl px-3 py-2.5 focus-within:ring-2 focus-within:ring-indigo-500 focus-within:border-transparent">
              <input
                type={show ? 'text' : 'password'}
                value={key}
                onChange={e => { setKey(e.target.value); setError(''); }}
                onKeyDown={e => e.key === 'Enter' && handleSave()}
                placeholder="sk-ant-api03-..."
                className="flex-1 text-sm bg-transparent outline-none text-slate-800 placeholder-slate-400 font-mono"
              />
              <button onClick={() => setShow(v => !v)} className="text-slate-400 hover:text-slate-600 transition-colors">
                {show ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
              </button>
            </div>
            {error && <p className="text-xs text-red-600 mt-1.5">{error}</p>}
            {existing && !error && (
              <p className="text-xs text-green-600 mt-1.5 flex items-center gap-1">
                <CheckCircle className="w-3 h-3" /> A key is currently saved
              </p>
            )}
          </div>

          {/* Get a key link */}
          <p className="text-xs text-slate-500">
            Don't have a key?{' '}
            <a
              href="https://console.anthropic.com/account/keys"
              target="_blank"
              rel="noopener noreferrer"
              className="text-indigo-600 hover:text-indigo-800 font-medium inline-flex items-center gap-0.5"
            >
              Get one at console.anthropic.com <ExternalLink className="w-3 h-3" />
            </a>
            {' '}— API usage is billed per message (typically $0.01-0.03 per query).
          </p>

          {/* Privacy note */}
          <div className="bg-slate-50 border border-slate-200 rounded-lg p-3">
            <p className="text-xs text-slate-500 leading-relaxed">
              <strong>Privacy:</strong> Your key is stored in your browser's localStorage
              and is only ever used to authenticate with Anthropic via a secure proxy.
              GENEie never stores it. Clear it anytime with the button below.
            </p>
          </div>

          {/* Actions */}
          <div className="flex gap-2 pt-1">
            <button
              onClick={handleSave}
              disabled={testing}
              className="flex-1 py-2.5 bg-indigo-600 hover:bg-indigo-700 disabled:opacity-50 text-white font-semibold rounded-xl transition-all text-sm"
            >
              {testing ? 'Verifying...' : 'Save Key'}
            </button>
            {existing && (
              <button
                onClick={handleClear}
                className="px-4 py-2.5 border border-red-200 text-red-600 hover:bg-red-50 rounded-xl transition-all text-sm font-medium"
              >
                Clear
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default APIKeyModal;